import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Player from '@vimeo/player';
import { Play, ArrowLeft, Lock, UserPlus, Share2, Check, Shuffle, ArrowRight } from 'lucide-react';
import { trackCta } from '@/lib/trackCta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_golf-coach-admin/artifacts/8hayyo5e_icon_vert_blanc-4k%20.png';

const getImgSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};

const extractVimeoId = (url) => {
  if (!url) return null;
  const m = String(url).match(/(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/);
  return m ? m[1] : null;
};

/**
 * PublicPreview — visitor-friendly page that showcases the first free program
 * without requiring an account. Lets prospects see what the app looks like
 * before signing up.
 */
export default function PublicPreview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [freePrograms, setFreePrograms] = useState([]);
  const [programIdx, setProgramIdx] = useState(0);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [activeExerciseIdx, setActiveExerciseIdx] = useState(0);
  const [shareCopied, setShareCopied] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const iframeRef = useRef(null);

  const program = freePrograms[programIdx] || null;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const r = await axios.get(`${API}/programs`);
        if (cancelled) return;
        const free = (r.data || []).filter((p) => p.is_free);
        if (free.length === 0) {
          setError('Aucun programme gratuit disponible pour le moment.');
          setLoading(false);
          return;
        }
        setFreePrograms(free);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Impossible de charger le programme gratuit.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  // Load details each time the active program changes.
  useEffect(() => {
    if (!program?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await axios.get(`${API}/programs/${program.id}/detail`);
        if (!cancelled) setDetails(d.data || null);
      } catch (_) {
        if (!cancelled) setDetails(null);
      }
    })();
    return () => { cancelled = true; };
  }, [program?.id]);

  const goToNextFreeProgram = useCallback(() => {
    if (freePrograms.length < 2) return;
    trackCta('decouverte', 'next_free_program', program?.id);
    const next = (programIdx + 1) % freePrograms.length;
    setProgramIdx(next);
    setActiveExerciseIdx(0);
    setDetails(null);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [freePrograms.length, programIdx, program?.id]);

  const introVimeoId = extractVimeoId(details?.introduction_video || program?.video_url);
  const conclusionVimeoId = extractVimeoId(details?.conclusion_video);
  const steps = Array.isArray(details?.steps) ? details.steps : [];

  // Build a full playlist: intro → each step → conclusion. All items playable
  // without an account since this program is flagged `is_free` by the admin.
  const playlist = [
    ...(introVimeoId ? [{
      kind: 'intro',
      title: 'Introduction',
      description: details?.introduction_text || '',
      vimeoId: introVimeoId,
    }] : []),
    ...steps.map((s, i) => ({
      kind: 'step',
      title: s.title || `Étape ${i + 1}`,
      description: s.description || '',
      vimeoId: extractVimeoId(s.video_url),
    })).filter((s) => s.vimeoId),
    ...(conclusionVimeoId ? [{
      kind: 'conclusion',
      title: 'Conclusion',
      description: details?.conclusion_text || '',
      vimeoId: conclusionVimeoId,
    }] : []),
  ];
  const currentItem = playlist[activeExerciseIdx] || playlist[0] || null;
  const activeVimeoId = currentItem?.vimeoId;
  const hasNextItem = activeExerciseIdx < playlist.length - 1;

  // Detect video end via Vimeo SDK so we can render our own "next step" overlay
  // and hide Vimeo's default end screen (related videos, follow button, etc.).
  useEffect(() => {
    setVideoEnded(false);
    if (!iframeRef.current || !activeVimeoId) return;
    let cancelled = false;
    let player;
    try {
      player = new Player(iframeRef.current);
      player.on('ended', () => { if (!cancelled) setVideoEnded(true); });
      player.on('play', () => { if (!cancelled) setVideoEnded(false); });
    } catch (_) { /* SDK attach failed — overlay simply won't appear */ }
    return () => {
      cancelled = true;
      try { player?.destroy?.(); } catch (_) {}
    };
  }, [activeVimeoId]);

  const goToNextItem = useCallback(() => {
    if (!hasNextItem) return;
    setActiveExerciseIdx((idx) => idx + 1);
    setVideoEnded(false);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hasNextItem]);

  const handleShare = useCallback(async () => {
    const shareUrl = window.location.origin + '/decouverte';
    const shareText = program?.title
      ? `Découvre gratuitement le programme « ${program.title} » sur CabGolf`
      : 'Découvre gratuitement un programme de golf de Laurent Cabanne sur CabGolf';
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: 'CabGolf', text: shareText, url: shareUrl });
        return;
      } catch (_) { /* user cancelled or unsupported — fall through to clipboard */ }
    }
    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (_) {
      window.prompt('Copie le lien pour partager :', shareUrl);
    }
  }, [program]);

  return (
    <div className="min-h-screen bg-[#0A0F0C] text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="public-preview-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#0A0F0C]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="preview-back-btn">
            <ArrowLeft className="w-4 h-4 text-white/70" />
            <img src={LOGO_URL} alt="CabGolf" className="h-7 w-auto" />
            <span className="text-white font-bold text-sm md:text-base tracking-tight">CabGolf</span>
          </Link>
          <button
            onClick={() => { trackCta('decouverte', 'inscription', program?.id); navigate('/?auth=register'); }}
            className="inline-flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-md bg-white text-black font-bold text-xs md:text-sm hover:bg-white/90 transition-colors"
            data-testid="preview-signup-btn"
          >
            <UserPlus size={14} />
            <span className="hidden sm:inline">Créer un compte</span>
            <span className="sm:hidden">Inscription</span>
          </button>
        </div>
      </header>

      {loading ? (
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-emerald-500" />
        </div>
      ) : error ? (
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <p className="text-white/80">{error}</p>
          <Link to="/" className="inline-block mt-4 px-4 py-2 rounded-md bg-white text-black font-semibold">Retour à l'accueil</Link>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 py-6 md:py-10">
          {/* Free tag */}
          <div className="mb-3">
            <span className="inline-flex items-center gap-1.5 text-[11px] md:text-xs font-black uppercase tracking-[0.2em] text-emerald-400">
              APERÇU GRATUIT · SANS COMPTE
            </span>
          </div>

          {/* Title + description + share */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-3 md:mb-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl md:text-5xl font-black tracking-tight text-white">
                {program?.title}
              </h1>
            </div>
            <button
              onClick={handleShare}
              className="self-start inline-flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs md:text-sm font-semibold transition-colors flex-shrink-0"
              data-testid="preview-share-btn"
              aria-label="Partager ce programme"
            >
              {shareCopied ? (
                <>
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span>Lien copié</span>
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4" />
                  <span>Partager</span>
                </>
              )}
            </button>
          </div>
          {program?.description && (
            <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-2xl mb-6 md:mb-8">
              {program.description.replace(/<[^>]*>/g, '').slice(0, 300)}
            </p>
          )}

          {/* Video player — native Vimeo iframe with controls (fullscreen supported). Uses key={vimeoId}
              so React remounts cleanly between videos. A custom overlay covers Vimeo's default end screen
              (related videos, follow button) when the video ends — replaced by our own "next step" CTA. */}
          <div className="relative w-full rounded-xl overflow-hidden mb-6 md:mb-8 bg-black" style={{ aspectRatio: '16/9' }}>
            {activeVimeoId ? (
              <>
                <iframe
                  key={activeVimeoId}
                  ref={iframeRef}
                  src={`https://player.vimeo.com/video/${activeVimeoId}?autoplay=1&controls=1&title=0&byline=0&portrait=0&dnt=1&playsinline=1&hide_related=1`}
                  title={currentItem?.title || program?.title}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  data-testid="preview-video-iframe"
                />

                {/* End-of-video custom overlay — hides Vimeo's stock end screen behind a clean CTA. */}
                {videoEnded && (
                  <div
                    className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-3 md:gap-4 p-4 md:p-6 text-center"
                    style={{ background: 'radial-gradient(ellipse at center, rgba(15,26,19,0.95) 0%, rgba(10,15,12,0.98) 60%, rgba(0,0,0,1) 100%)' }}
                    data-testid="preview-end-overlay"
                  >
                    {hasNextItem ? (
                      <>
                        <p className="text-emerald-300 text-xs md:text-sm uppercase tracking-[0.2em] font-black">
                          Vidéo terminée
                        </p>
                        <p className="text-white text-lg md:text-2xl font-black tracking-tight max-w-md">
                          Prêt pour la suite ?
                        </p>
                        <button
                          onClick={goToNextItem}
                          data-testid="preview-next-step-btn"
                          className="inline-flex items-center gap-2 px-5 md:px-7 py-3 md:py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-sm md:text-base shadow-2xl transition-transform hover:scale-[1.03] active:scale-95"
                        >
                          <span>Étape suivante</span>
                          <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                        <button
                          onClick={() => setVideoEnded(false)}
                          className="text-white/50 hover:text-white text-xs md:text-sm underline underline-offset-4"
                        >
                          Revoir la vidéo
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-emerald-300 text-xs md:text-sm uppercase tracking-[0.2em] font-black">
                          Programme terminé
                        </p>
                        <p className="text-white text-lg md:text-2xl font-black tracking-tight max-w-md">
                          Bravo ! Débloque maintenant tout CabGolf.
                        </p>
                        <button
                          onClick={() => { trackCta('decouverte', 'essayer', program?.id); navigate('/?auth=register'); }}
                          data-testid="preview-end-cta"
                          className="inline-flex items-center gap-2 px-5 md:px-7 py-3 md:py-3.5 rounded-full text-white font-bold text-sm md:text-base shadow-2xl transition-transform hover:scale-[1.03] active:scale-95"
                          style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
                        >
                          <Play className="w-4 h-4 md:w-5 md:h-5 fill-current" />
                          Essayer gratuitement
                        </button>
                        <button
                          onClick={() => setVideoEnded(false)}
                          className="text-white/50 hover:text-white text-xs md:text-sm underline underline-offset-4"
                        >
                          Revoir la vidéo
                        </button>
                      </>
                    )}
                  </div>
                )}
              </>
            ) : program?.image_url ? (
              <img src={getImgSrc(program.image_url)} alt={program.title} className="w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/40">Aperçu vidéo indisponible</div>
            )}
          </div>

          {/* Full playlist (intro + steps + conclusion). All items are unlocked because the program is admin-flagged is_free. */}
          {playlist.length > 0 && (
            <div className="mb-8 md:mb-10">
              <div className="flex items-baseline justify-between gap-2 mb-3 md:mb-4">
                <h2 className="text-lg md:text-xl font-bold text-white">
                  Contenu du programme
                </h2>
                <span className="text-white/50 text-xs md:text-sm">
                  {playlist.length} vidéo{playlist.length > 1 ? 's' : ''} · Accès libre
                </span>
              </div>
              <div className="grid grid-cols-1 gap-2 md:gap-3">
                {playlist.map((item, idx) => {
                  const isActive = idx === activeExerciseIdx;
                  let badge;
                  if (item.kind === 'intro') badge = 'Intro';
                  else if (item.kind === 'conclusion') badge = 'Conclusion';
                  else {
                    const stepNo = playlist.slice(0, idx).filter((p) => p.kind === 'step').length + 1;
                    badge = `Étape ${stepNo}`;
                  }
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        setActiveExerciseIdx(idx);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`text-left rounded-lg border p-3 md:p-4 transition-all ${
                        isActive
                          ? 'bg-emerald-500/10 border-emerald-400/40'
                          : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.08]'
                      }`}
                      data-testid={`preview-step-${idx}`}
                    >
                      <div className="flex items-center gap-3 md:gap-4">
                        <div className={`w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/70'}`}>
                          {isActive ? <Play className="w-4 h-4 fill-current" /> : <span className="font-bold text-sm">{idx + 1}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-block text-[10px] md:text-xs font-black uppercase tracking-widest ${isActive ? 'text-emerald-300' : 'text-white/40'}`}>{badge}</span>
                            {isActive && <span className="text-emerald-300 text-[10px] md:text-xs">En lecture</span>}
                          </div>
                          <p className="text-white font-semibold text-sm md:text-base truncate">{item.title}</p>
                          {item.description && (
                            <p className="text-white/55 text-xs mt-1 line-clamp-2">
                              {String(item.description).replace(/<[^>]*>/g, '').slice(0, 200)}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* CTA card */}
          <div className="relative rounded-2xl overflow-hidden border border-emerald-400/30" style={{ background: 'linear-gradient(135deg, #0F1A13 0%, #142219 60%, #0A0F0C 100%)' }}>
            <div className="relative p-6 md:p-10 text-center">
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30">
                <Lock size={12} className="text-emerald-400" />
                <span className="text-emerald-300 text-[11px] font-black uppercase tracking-widest">Aimé ce programme ?</span>
              </div>
              <h3 className="text-white text-2xl md:text-4xl font-black tracking-tight mb-3">
                Débloque tout CabGolf
              </h3>
              <p className="text-white/70 text-sm md:text-base max-w-lg mx-auto mb-6">
                Accède à tous les autres programmes, à l'analyse vidéo par Laurent et à ton coach IA en créant ton compte.
              </p>
              <div className="flex flex-nowrap items-center justify-center gap-2 md:gap-3">
                <button
                  onClick={() => { trackCta('decouverte', 'essayer', program?.id); navigate('/?auth=register'); }}
                  className="inline-flex items-center gap-2 px-5 md:px-8 py-3 md:py-3.5 rounded-md font-bold text-sm md:text-base text-white shadow-2xl hover:scale-[1.03] active:scale-95 transition-transform"
                  style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
                  data-testid="preview-cta-signup"
                >
                  <Play className="w-4 h-4 md:w-5 md:h-5 fill-current" />
                  Essayer gratuitement
                </button>
                <button
                  onClick={() => { trackCta('decouverte', 'login', program?.id); navigate('/?auth=login'); }}
                  className="inline-flex items-center gap-2 px-5 md:px-6 py-3 md:py-3.5 rounded-md font-bold text-sm md:text-base bg-white/15 backdrop-blur-md text-white hover:bg-white/25 transition-colors"
                  data-testid="preview-cta-login"
                >
                  Se connecter
                </button>
              </div>
            </div>
          </div>

          {/* "Voir un autre programme gratuit" — loops through additional free programs
              flagged is_free by the admin. Shown only when at least one other free program exists. */}
          {freePrograms.length > 1 && (
            <div className="mt-6 md:mt-8 flex justify-center">
              <button
                onClick={goToNextFreeProgram}
                data-testid="preview-next-free-program"
                className="inline-flex items-center gap-2 px-5 md:px-7 py-3 md:py-3.5 rounded-full bg-emerald-600/90 hover:bg-emerald-500 text-white font-bold text-sm md:text-base border border-emerald-400/40 transition-all hover:scale-[1.03] active:scale-95 shadow-lg"
              >
                <Shuffle className="w-4 h-4 md:w-5 md:h-5" />
                <span>Voir un autre programme gratuit</span>
                <span className="text-emerald-200/80 text-xs font-semibold ml-1">
                  ({(programIdx % freePrograms.length) + 1}/{freePrograms.length})
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
